import { useState, Fragment, useEffect, useRef } from 'react';
import { IconEdit, IconTrash } from '@tabler/icons-react';
import { Box, Button, Divider, Collapse, IconButton, ListSubheader, MenuItem, Radio, Select, TextField, Typography } from '@mui/material';
import { IconChevronDown, IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { fetchCohortOptions, deleteCohort } from 'features/frontoffice/actions/actions';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppConfig } from 'AppConfig';
import { hasPermission } from 'utils/common';
import routePaths from 'routes/routePaths';
import { buttonOpenCohortBuilderdSx, cohortEditIconButtonSx, cohortDeleteIconButtonSx, cohortInFieldEditIconButtonSx, cohortInFieldDeleteIconButtonSx, cohortMenuItemLabelSx, cohortMenuItemRowSx,
    cohortSelectFieldWrapperSx, cohortSelectWithEditIconSx, colors, errorTextSx, filterSectionLabelSx, getCohortMenuItemSx,
    getCohortMenuProps, getErrorFieldSx, getResetButtonSx, getTimeWindowSelectSx,
    helperTextSx, listSubHeaderSx, personIdFieldSx, radioSx, selectionPanelSx, expandedHeaderSx, collapsedHeaderSx,
    personIdWarningTextSx, radioOptionRowSx, inactivePopulationSelectSx, inactivePersonIdFieldSx
} from '../globalFiltersStyles';
import { POPULATION_MODE, COHORT_CATEGORIES } from 'common/Constant';
import StyledTooltip from 'common/StyledTooltip';
import FilterSection from './FilterSection';
import DeleteCohortDialog from './DeleteCohortDialog';

const getCohortDisplayLabel = (value, cohortOptions, cohortDisplayName) => {
  if (!value) return 'Select a Cohort';
  const allCohorts = Object.values(cohortOptions || {}).flat();
  return allCohorts.find((o) => o.value === value)?.label || cohortDisplayName || value;
};

const normalizeTenantCd = (value) => (typeof value === 'string' ? value.trim() : '');

// Personal cohorts are always owned by the current user; team cohorts are only
// editable by the user who created them.
const isCohortOptionEditable = (categoryKey, option, currentUsername) => {
  if (!option) return false;
  if (categoryKey === 'myCohorts') return true;
  return Boolean(currentUsername) && Boolean(option.owner) && option.owner === currentUsername;
}
  
const selectedCohortLabelSx = {
  display: 'block',
  width: 'calc(100% - 8px)',
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const PopulationFilters = (props) => {
  const { effectiveTenantValue, cohortValue, onCohortChange, personIdValue, personIdError, onPersonIdChange,
      personIdWarningMessage, personIdValidationLoading, onPersonIdBlur, onPersonIdReset,onPopulationReset, populationMode, onPopulationModeChange, cohortPersonIdsLoading, onClose, cohortDisplayName, open,
      onOpenCohortBuilder, focusCohortSelectionRequestKey,
  } = props;

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const cohortOptions = useSelector((state) => state.fo?.cohortOptions);
  const permissions = useSelector((state) => state.user?.permissions);
  const currentUsername = useSelector((state) => state.user?.username);
  const hasAdHocPermission = hasPermission(AppConfig.PERMISSION.FO_AD_HOC, permissions);
  const [isCohortOpen, setIsCohortOpen] = useState(false);
  const [expandedCohortCategories, setExpandedCohortCategories] = useState({ myCohorts: false, sharedCohorts: false });
  const [isPersonIdFieldFocused, setIsPersonIdFieldFocused] = useState(false);
  const [personIdFieldHeight, setPersonIdFieldHeight] = useState(200);
  const [cohortMenuWidth, setCohortMenuWidth] = useState(null);
  const cohortSelectRef = useRef(null);
  const cohortModeRadioRef = useRef(null);
  const populationSectionRef = useRef(null);
  const handledFocusRequestKeyRef = useRef(0);
  const personIdResizeStartRef = useRef(null);

  const getCohortCategoryKeyForValue = (value) => {
    if (!value) return null;
    return COHORT_CATEGORIES.find(({ key }) => cohortOptions?.[key]?.some((option) => option.value === value))?.key || null;
  };

  const selectedCohortCategory = getCohortCategoryKeyForValue(cohortValue);
  const isCohortActive = populationMode === POPULATION_MODE.COHORT;
  const isPersonIdActive = populationMode === POPULATION_MODE.PERSON_ID;
  const isSelectedCohortInOptions = Object.values(cohortOptions || {})
    .flat()
    .some((option) => option?.value === cohortValue);
  const selectedCohortOption = (cohortOptions?.[selectedCohortCategory] || [])
    .find((option) => option.value === cohortValue) || null;
  const isSelectedCohortEditable = isCohortOptionEditable(selectedCohortCategory, selectedCohortOption, currentUsername);

  const isCohortOptionsEmpty = !cohortOptions?.myCohorts?.length && !cohortOptions?.sharedCohorts?.length;
  const isAnyCohortOptionsEmpty = !cohortOptions?.myCohorts?.length || !cohortOptions?.sharedCohorts?.length;
  const isAnyCohortCategoryExpanded = Object.values(expandedCohortCategories).some(Boolean);

  useEffect(() => {
    if (!isCohortOpen) {
      setExpandedCohortCategories({
        myCohorts: false,
        sharedCohorts: false,
      });
    }
  }, [isCohortOpen]);

  const toggleCohortCategory = (key) => {
    setExpandedCohortCategories((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  }

  const handlePersonIdChange = (event) => {
    onPersonIdChange(event);
  };

  const handlePersonIdBlur = () => {
    setIsPersonIdFieldFocused(false);
    if (onPersonIdBlur) {
      onPersonIdBlur();
    }
  };

  const handlePersonIdFocus = () => {
    setIsPersonIdFieldFocused(true);
    if (populationMode !== POPULATION_MODE.PERSON_ID) {
      onPopulationModeChange(POPULATION_MODE.PERSON_ID);
    }
  };

  const handlePersonIdResizeStart = (event) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    personIdResizeStartRef.current = {
      pointerY: event.clientY,
      fieldHeight: personIdFieldHeight,
    };
  };

  const handlePersonIdResize = (event) => {
    if (!personIdResizeStartRef.current) return;

    const { pointerY, fieldHeight } = personIdResizeStartRef.current;
    setPersonIdFieldHeight(Math.max(200, fieldHeight + event.clientY - pointerY));
  };

  const handlePersonIdResizeEnd = (event) => {
    personIdResizeStartRef.current = null;
    event.currentTarget.releasePointerCapture?.(event.pointerId);
  };

  const handlePersonIdResizeKeyDown = (event) => {
    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return;

    event.preventDefault();
    const direction = event.key === 'ArrowDown' ? 1 : -1;
    setPersonIdFieldHeight((height) => Math.max(200, height + (direction * 12)));
  };

  const personIdDisplayError = personIdError;
  const personIdDisplayWarning = personIdWarningMessage || '';
  const hasPopulationSelection = isCohortActive || isPersonIdActive || Boolean(cohortValue) || Boolean(personIdValue);

  // Fetch cohort options when the drawer opens and on tenant switch
  useEffect(() => {
    if (open && effectiveTenantValue) {
      dispatch(fetchCohortOptions(effectiveTenantValue));
    }
  }, [open, effectiveTenantValue, dispatch]);

  // Fetch cohort options when the menu opens
  const handleMenuOpen = () => {
    setCohortMenuWidth(cohortSelectRef.current?.getBoundingClientRect().width || null);
    setIsCohortOpen(true);
    if (populationMode !== POPULATION_MODE.COHORT) {
      onPopulationModeChange(POPULATION_MODE.COHORT);
    }
    if (effectiveTenantValue) {
      dispatch(fetchCohortOptions(effectiveTenantValue));
    }
    if (selectedCohortCategory) {
      setExpandedCohortCategories({
        myCohorts: false,
        sharedCohorts: false,
        [selectedCohortCategory]: true,
      });
    }
  };

  // Launches Cohort Builder; when editOption is provided it opens directly in
  // edit mode with that cohort's workbook loaded, without changing selection.
  const navigateToCohortBuilder = (editOption) => {
    const tenantCd = normalizeTenantCd(effectiveTenantValue);
    const cohortBuilderFlowId = onOpenCohortBuilder?.() || null;
    const cohortBuilderLocation = {
      pathname: routePaths.cohortBuilder,
      ...(tenantCd ? { search: `?tenantCd=${encodeURIComponent(tenantCd)}` } : {}),
    };

    onClose?.();
    navigate(cohortBuilderLocation, {
      state: {
        tenantCd: tenantCd || null,
        ...(cohortBuilderFlowId ? { cohortBuilderFlowId } : {}),
        openedFromGlobalFilters: true,
        returnLocation: {
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
          state: location.state,
        },
        ...(editOption ? {
          editCohort: {
            luid: editOption.value,
            reptSource: editOption.reptSource || null,
            displayText: editOption.label,
          },
        } : {}),
      },
    });
  };

  const handleEditCohortClick = (event, option) => {
    event.preventDefault();
    event.stopPropagation();
    setIsCohortOpen(false);
    navigateToCohortBuilder(option);
  };

  const [cohortPendingDelete, setCohortPendingDelete] = useState(null);

  const handleDeleteCohortClick = (event, option) => {
    event.preventDefault();
    event.stopPropagation();
    setIsCohortOpen(false);
    setCohortPendingDelete(option);
  };

  const handleCloseDeleteDialog = () => {
    setCohortPendingDelete(null);
  };

  const handleConfirmDeleteCohort = async () => {
    if (!cohortPendingDelete || !effectiveTenantValue) {
      return;
    }
    const deletedValue = cohortPendingDelete.value;
    const result = await dispatch(deleteCohort(effectiveTenantValue, deletedValue));
    if (result?.ok && cohortValue === deletedValue) {
      onCohortChange?.('');
      onPopulationReset?.();
    }
  };
  
  useEffect(() => {
    if (!open || !focusCohortSelectionRequestKey || handledFocusRequestKeyRef.current === focusCohortSelectionRequestKey) {
      return;
    }

    handledFocusRequestKeyRef.current = focusCohortSelectionRequestKey;

    const rafId = window.requestAnimationFrame(() => {
      const populationSection = populationSectionRef.current;
      if (!populationSection) {
        return;
      }

      const focusTarget = cohortSelectRef.current?.querySelector?.('[role="combobox"]')
        || cohortSelectRef.current
        || cohortModeRadioRef.current;

      // Use the drawer's scroll container so the full population controls are visible.
      const scrollContainer = populationSection.closest?.('[data-testid="global-filters-scroll-container"]');
      if (scrollContainer) {
        const topOffset = Math.max(populationSection.offsetTop - 24, 0);
        if (typeof scrollContainer.scrollTo === 'function') {
          scrollContainer.scrollTo({ top: topOffset, behavior: 'auto' });
        } else {
          scrollContainer.scrollTop = topOffset;
        }
      } else {
        populationSection.scrollIntoView?.({ block: 'start', inline: 'nearest' });
      }

      focusTarget?.focus?.({ preventScroll: true });
    });

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [open, focusCohortSelectionRequestKey]);

	return (
    <Box display="flex" flexDirection="column" gap={1} width="100%">
      <FilterSection
        action={hasPopulationSelection ? (
          <Button
            variant="outlined"
            aria-label="Reset Population"
            onClick={onPopulationReset}
            sx={getResetButtonSx(true)}
          >
            Reset
          </Button>
        ) : null}
        label="Population (Optional)"
        helperText="Define who to include using a saved cohort or specific Person IDs."
        helperPlacement="above"
      >
        <Box display="flex" flexDirection="column" gap={3.5} sx={selectionPanelSx}>
        <Box ref={populationSectionRef} display="flex" flexDirection="column" gap={1} width="100%">
					<Box display="flex" gap={1} sx={radioOptionRowSx}>
						<Radio
              inputRef={cohortModeRadioRef}
							checked={populationMode === POPULATION_MODE.COHORT}
							onChange={() => onPopulationModeChange(POPULATION_MODE.COHORT)}
							disabled={isCohortOptionsEmpty}
							inputProps={{ 'aria-label': 'Use Cohort' }}
							sx={radioSx}
						/>
						<Typography sx={filterSectionLabelSx}>Use Cohort</Typography>
					</Box>
					<Typography sx={helperTextSx}>
						{cohortPersonIdsLoading
						? 'Loading person IDs for the selected cohort.'
						: isCohortOptionsEmpty
						? (hasAdHocPermission
							? 'You have not created any cohorts yet. Use the Cohort Builder to create one.'
							: 'No cohorts available.')
						: 'Select a saved group of members created in Cohort Builder.'}
					</Typography>
					<Box display="flex" flexDirection="column" gap={2} width="100%">
            {(!isCohortOptionsEmpty || Boolean(cohortValue)) && (
            <Box sx={cohortSelectFieldWrapperSx}>
							<Select
                ref={cohortSelectRef}
								variant="outlined"
								value={cohortValue}
								onChange={(event) => onCohortChange(event.target.value)}
								onClose={() => setIsCohortOpen(false)}
								onOpen={handleMenuOpen}
								open={isCohortOpen}
								IconComponent={(props) => <IconChevronDown {...props} stroke={2} size={20} color={colors.iconFill} />}
								displayEmpty
								fullWidth
								disabled={isCohortOptionsEmpty || cohortPersonIdsLoading}
                renderValue={(value) => {
                  const displayLabel = getCohortDisplayLabel(value, cohortOptions, cohortDisplayName);
                  const content = (
                    <Box component="span" sx={selectedCohortLabelSx}>{displayLabel}</Box>
                  );

                  return value ? (
                    <StyledTooltip title={displayLabel} placement="top">
                      {content}
                    </StyledTooltip>
                  ) : content;
                }}
                MenuProps={getCohortMenuProps(isCohortOpen, cohortMenuWidth)}
								inputProps={{ 'aria-label': 'Select a Cohort', 'data-inactive': !isCohortActive }}
								sx={[getTimeWindowSelectSx(isCohortOpen), !isCohortActive && inactivePopulationSelectSx, Boolean(cohortValue) && isSelectedCohortEditable && cohortSelectWithEditIconSx]}
							>
                {cohortValue && !isSelectedCohortInOptions ? (
                  <MenuItem value={cohortValue} sx={{ display: 'none' }} />
                ) : null}
								{isAnyCohortOptionsEmpty ? 
                  Object.entries(cohortOptions || {})
                    .filter(([, sectionItems]) => Array.isArray(sectionItems) && sectionItems.length > 0)
                    .flatMap(([sectionKey, sectionItems], sectionIndex, sectionsArray) => [
                      <ListSubheader key={`header-${sectionKey}`} sx={listSubHeaderSx}>
                        {COHORT_CATEGORIES.find(({ key }) => key === sectionKey)?.label
                          || sectionKey.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (char) => char.toUpperCase())}
                      </ListSubheader>,
                      ...sectionItems.map((option, index) => {
                        const isEditable = isCohortOptionEditable(sectionKey, option, currentUsername);
                        return (
                        <MenuItem
                          key={option.value}
                          value={option.value}
                          selected={cohortValue === option.value}
                          sx={getCohortMenuItemSx(cohortValue === option.value, index, sectionItems.length)}
                        >
                          <Box sx={cohortMenuItemRowSx}>
                            <Typography component="span" sx={cohortMenuItemLabelSx}>{option.label}</Typography>
                            {isEditable && (
                              <>
                                <IconButton
                                  size="small"
                                  aria-label={`Edit ${option.label}`}
                                  onMouseDown={(event) => event.stopPropagation()}
                                  onClick={(event) => handleEditCohortClick(event, option)}
                                  sx={cohortEditIconButtonSx}
                                >
                                  <IconEdit size={16} stroke={2} />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  aria-label={`Delete ${option.label}`}
                                  onMouseDown={(event) => event.stopPropagation()}
                                  onClick={(event) => handleDeleteCohortClick(event, option)}
                                  sx={cohortDeleteIconButtonSx}
                                >
                                  <IconTrash size={16} stroke={2} />
                                </IconButton>
                              </>
                            )}
                          </Box>
                        </MenuItem>
                        );
                      }),
                      ...(sectionIndex < sectionsArray.length - 1
                        ? [<Divider key={`divider-${sectionKey}`} sx={{ my: 0, borderColor: colors.dividerStroke }} />]
                        : []),
                    ]
                  ) : 
                  COHORT_CATEGORIES.map(({ key, label }) => {
                    const categoryItems = cohortOptions?.[key] || [];
                    const isExpanded = expandedCohortCategories[key];
                    
                    if (isAnyCohortCategoryExpanded && !isExpanded) {
                      return null;
                    }

                    return (
                      <Fragment key={key}>
                        {!isExpanded && (
                          <ListSubheader disableSticky>
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="space-between"
                              alignSelf="stretch"
                              onClick={() => toggleCohortCategory(key)}
                              sx={{ 
                                cursor: 'pointer',
                                py: '16px',
                                background: '#FFF'
                              }}
                            >
                              <Typography sx={collapsedHeaderSx}>
                                {label}
                              </Typography>
                              <IconChevronRight size={20} color={colors.iconFill} />
                            </Box>
                          </ListSubheader>
                        )}
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box
                            display="flex"
                            alignItems="center"
                            alignSelf="stretch"
                            gap="8px"
                            onClick={() => toggleCohortCategory(key)}
                            sx={{ cursor: 'pointer', padding: '16px 16px 8px 16px' }}
                          >
                            <IconChevronLeft size={20} color={colors.iconFill} />
                            <Typography sx={expandedHeaderSx}>
                              {label}
                            </Typography>
                          </Box>
                          {categoryItems.map((option, index) => {
                            const isSelected = cohortValue === option.value;
                            const isEditable = isCohortOptionEditable(key, option, currentUsername);
                            return (
                              <MenuItem
                                key={option.value}
                                value={option.value}
                                selected={isSelected}
                                onClick={() => {
                                  onCohortChange(option.value);
                                  setIsCohortOpen(false);
                                }}
                                sx={getCohortMenuItemSx(isSelected, index, categoryItems.length)}
                              >
                                <Box sx={cohortMenuItemRowSx}>
                                  <Typography component="span" sx={cohortMenuItemLabelSx}>{option.label}</Typography>
                                  {isEditable && (
                                    <>
                                      <IconButton
                                        size="small"
                                        aria-label={`Edit ${option.label}`}
                                        onMouseDown={(event) => event.stopPropagation()}
                                        onClick={(event) => handleEditCohortClick(event, option)}
                                        sx={cohortEditIconButtonSx}
                                      >
                                        <IconEdit size={16} stroke={2} />
                                      </IconButton>
                                      <IconButton
                                        size="small"
                                        aria-label={`Delete ${option.label}`}
                                        onMouseDown={(event) => event.stopPropagation()}
                                        onClick={(event) => handleDeleteCohortClick(event, option)}
                                        sx={cohortDeleteIconButtonSx}
                                      >
                                        <IconTrash size={16} stroke={2} />
                                      </IconButton>
                                    </>
                                  )}
                                </Box>
                              </MenuItem>
                            );
                          })}
                        </Collapse>
                      </Fragment>
                    );
								  })}
							</Select>
            {Boolean(cohortValue) && isSelectedCohortEditable && (
              <>
                <IconButton
                  size="small"
                  aria-label={`Edit ${getCohortDisplayLabel(cohortValue, cohortOptions, cohortDisplayName)}`}
                  onMouseDown={(event) => event.stopPropagation()}
                  onClick={(event) => {
                    event.stopPropagation();
                    navigateToCohortBuilder(selectedCohortOption);
                  }}
                  sx={cohortInFieldEditIconButtonSx}
                >
                  <IconEdit size={18} stroke={2} />
                </IconButton>
                <IconButton
                  size="small"
                  aria-label={`Delete ${getCohortDisplayLabel(cohortValue, cohortOptions, cohortDisplayName)}`}
                  onMouseDown={(event) => event.stopPropagation()}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleDeleteCohortClick(event, selectedCohortOption);
                  }}
                  sx={cohortInFieldDeleteIconButtonSx}
                >
                  <IconTrash size={18} stroke={2} />
                </IconButton>
              </>
            )}
            </Box>
						)}
						{hasAdHocPermission && (
						<Button
							variant="outlined"
              onClick={() => navigateToCohortBuilder()}
							sx={{...buttonOpenCohortBuilderdSx, mt: isCohortOptionsEmpty ? 1 : 0}}>
							Open Cohort Builder
						</Button>
						)}
					</Box>
				</Box>
				<Box display="flex" flexDirection="column" gap={1}>
					<Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
						<Box display="flex" gap={1} sx={radioOptionRowSx}>
							<Radio
								checked={populationMode === POPULATION_MODE.PERSON_ID}
								onChange={() => onPopulationModeChange(POPULATION_MODE.PERSON_ID)}
								inputProps={{ 'aria-label': 'Use Person IDs' }}
								sx={radioSx}
							/>
							<Typography sx={filterSectionLabelSx}>Use Person IDs</Typography>
						</Box>
						<Button
							variant="outlined"
							onClick={onPersonIdReset}
							disabled={populationMode !== POPULATION_MODE.PERSON_ID}
							sx={getResetButtonSx(personIdValue)}>
							Reset
						</Button>
					</Box>
					<Typography sx={{ ...helperTextSx, px: 0 }}>Paste or enter Person IDs, separated by commas, semicolons, or line breaks.</Typography>
          {personIdValidationLoading ? <Typography sx={{ ...helperTextSx, px: 0 }}>Validating Person IDs...</Typography> : null}
					<Box display="flex" flexDirection="column" gap={personIdError ? 1 : 0} width="100%">
            <Box
              position="relative"
              sx={{ '--person-id-field-height': personIdFieldHeight + 'px' }}
            >
						<TextField
							variant="outlined"
							placeholder="Enter Person IDs"
							multiline
							rows={3}
							value={personIdValue}
							onChange={handlePersonIdChange}
							onFocus={handlePersonIdFocus}
							onBlur={handlePersonIdBlur}
							inputProps={{ 'aria-label': 'Person ID', 'data-inactive': !isPersonIdActive }}
							sx={[personIdDisplayError ? getErrorFieldSx(personIdFieldSx) : personIdFieldSx, !isPersonIdActive && inactivePersonIdFieldSx]}
							fullWidth
						/>
              <Box
                role="separator"
                aria-label="Resize Person ID field"
                aria-orientation="horizontal"
                aria-valuemin={200}
                aria-valuenow={Math.round(personIdFieldHeight)}
                tabIndex={0}
                onPointerDown={handlePersonIdResizeStart}
                onPointerMove={handlePersonIdResize}
                onPointerUp={handlePersonIdResizeEnd}
                onPointerCancel={handlePersonIdResizeEnd}
                onKeyDown={handlePersonIdResizeKeyDown}
                sx={{
                  position: 'absolute',
                  right: '16px',
                  bottom: '16px',
                  width: '16px',
                  height: '16px',
                  cursor: 'ns-resize',
                  touchAction: 'none',
                  zIndex: 2,
                  clipPath: 'polygon(100% 0, 100% 100%, 0 100%)',
                  background: 'repeating-linear-gradient(135deg, transparent 0 3px, ' + colors.iconFill + ' 3px 4px)',
                }}
              />
            </Box>
						{personIdDisplayError ? <Typography sx={errorTextSx}>{personIdDisplayError}</Typography> : null}
					</Box>
        </Box>
      </Box>
      </FilterSection>
      {personIdDisplayWarning && (!isPersonIdFieldFocused || populationMode === POPULATION_MODE.COHORT)
        ? <Typography sx={personIdWarningTextSx}>{personIdDisplayWarning}</Typography>
        : null}
      <DeleteCohortDialog
        open={Boolean(cohortPendingDelete)}
        onClose={handleCloseDeleteDialog}
        onConfirm={handleConfirmDeleteCohort}
        cohortName={cohortPendingDelete?.label || ''}
      />
    </Box>
	);
};

export default PopulationFilters;